type Falsy = false | 0 | '' | null | undefined;

type DefinitelyTruthy<T> = false extends T
  ? never
  : 0 extends T
    ? never
    : '' extends T
      ? never
      : null extends T
        ? never
        : undefined extends T
          ? never
          : T;

export const isTruthy = <T>(x: T | Falsy): x is DefinitelyTruthy<T> => Boolean(x);

export const isNotUndefined = <T>(value: T | undefined): value is T => value !== undefined;

export const isNotNullish = <T>(value: T): value is NonNullable<T> =>
  value !== undefined && value !== null;

export const belongsToEnum = <T extends string>(value: string, myEnum: Record<T, T>): value is T =>
  value in myEnum;

export const typeSafeObjectKeys = <T extends object>(object: T) =>
  Object.keys(object) as (keyof T & string)[];

export const typeSafeObjectValues = <K extends string, T>(object: { [_ in K]: T }): T[] =>
  Object.values(object);

export const typeSafeObjectEntries = <T extends object>(object: T) =>
  Object.entries(object) as [keyof T, T[keyof T]][];

export const typeSafeFromEntries = <T extends string, U>(
  entries: Iterable<readonly [T, U]>,
): Record<T, U> => Object.fromEntries(entries) as Record<T, U>;

class Unreachable extends Error {
  constructor(value: never) {
    super(
      `Unexpected type of value: ${typeof value}.
This usually happens when a union type is extended to support
a new sub type that is not handled by the type checker.
Have you added a new API type that is not handled yet by the frontend?
Here's an example:

type Row = CommentRow | FormulaRow;

if (row.__typename === 'CommentRow') {
} else if (row.__typename === 'FormulaRow') {
} else {
  // This was supposed to never happen, but since a new type was added,
  // this branch can be reached.
  assertUnreachable(row.__typename);
}
`,
    );
  }
}

/**
 * @throws {Unreachable}
 */
export function assertUnreachable(value: never): never {
  throw new Unreachable(value);
}

/**
 * Takes a type predicate function and returns the type it asserts.
 *
 * @example
 * ```
 * const hasSomeField = (x: Foo) => x.someField !== undefined;
 * type Test1 = GuardType<typeof hasSomeField>; // Foo
 *
 * const isSubtype = (x: Foo): x is FooSubtype => x.someField !== undefined;
 * type Test2 = GuardType<typeof isSubtype>; // FooSubtype
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GuardType<T extends (x: any) => boolean> = T extends (x: infer A) => boolean
  ? T extends ((x: A) => x is infer B extends A)
    ? B
    : A
  : never;

export type ObjectWithoutGqlTypename<O extends object> = O extends { __typename: string }
  ? {
      [K in Exclude<keyof O, '__typename'>]: O[K] extends object
        ? ObjectWithoutGqlTypename<O[K]>
        : O[K];
    } & { __typename?: unknown }
  : O;
